/** @jsx jsx */
import React, { useState, useEffect } from 'react';
import { styled, css, jsx } from '@compiled/react';
import range from 'lodash/range';
import { graphql, usePaginationFragment } from 'react-relay';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { WidthObserver } from '@atlaskit/width-detector';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { designCarousel_versionIssueAssociatedDesigns$key } from '@atlassian/jira-relay/src/__generated__/designCarousel_versionIssueAssociatedDesigns.graphql';
import type { versionDetailsDesignRefetchableQuery } from '@atlassian/jira-relay/src/__generated__/versionDetailsDesignRefetchableQuery.graphql';
import { CARD_GAP, CARD_WIDTH } from '../../common/constants';
import { DesignCardSkeleton } from '../../common/ui/design-card-skeleton';
import { Arrow } from './arrow';
import { DesignCard } from './design-card';
import messages from './messages';

interface Props {
	jiraVersion: designCarousel_versionIssueAssociatedDesigns$key;
}

const getNumFullyRenderedCards = (carouselWidth: number): number =>
	Math.floor(carouselWidth / CARD_WIDTH);
// curPageNum: 0-based, returned number: 1-based(displayed to user)
const getCurPageStartEndNum = (
	fullyRenderedCardNum: number,
	curPageNum: number,
	totalCount: number,
): { currentStart: number; currentEnd: number; lastPageNum: number } => ({
	currentStart: curPageNum * fullyRenderedCardNum + 1,
	currentEnd:
		totalCount >= (curPageNum + 1) * fullyRenderedCardNum
			? (curPageNum + 1) * fullyRenderedCardNum
			: totalCount,
	lastPageNum: Math.ceil(totalCount / fullyRenderedCardNum) - 1,
});

export const DesignCarousel = ({ jiraVersion }: Props) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [carouselWidth, setCarouselWidth] = useState<number>(0);
	const [curPageNum, setCurPageNum] = useState<number>(0);
	const { data, hasNext, loadNext, isLoadingNext } = usePaginationFragment<
		versionDetailsDesignRefetchableQuery,
		designCarousel_versionIssueAssociatedDesigns$key
	>(
		graphql`
			fragment designCarousel_versionIssueAssociatedDesigns on JiraVersion
			@argumentDefinitions(
				first: { type: "Int", defaultValue: 6 }
				after: { type: "String", defaultValue: null }
			)
			@refetchable(queryName: "versionDetailsDesignRefetchableQuery") {
				...designCard_versionIssueAssociatedDesigns_jiraVersion
				issueAssociatedDesigns(
					first: $first
					after: $after
					sort: { createdAt: { direction: DESC, priority: 1 } }
				)
					@optIn(to: "JiraVersionIssueAssociatedDesigns")
					@connection(key: "DesignSlider_issueAssociatedDesigns")
					@required(action: THROW) {
					edges {
						node {
							... on DevOpsDesign {
								id
								...designCard_versionIssueAssociatedDesigns_devOpsDesign
							}
						}
					}
					totalCount @required(action: THROW)
				}
			}
		`,
		jiraVersion,
	);

	const designEdges = data.issueAssociatedDesigns?.edges ?? [];
	const designNodes = designEdges
		.filter(Boolean)
		.map((edge) => edge.node)
		.filter(Boolean);

	// We only need to use the potentially inaccurate/outdated `totalCount` value from
	// the API if we don't have all the edges loaded.
	const totalCount = hasNext ? data.issueAssociatedDesigns.totalCount : designNodes?.length ?? 0;

	const numFullyRenderedCards = getNumFullyRenderedCards(carouselWidth);
	const carouselPageInfo = getCurPageStartEndNum(numFullyRenderedCards, curPageNum, totalCount);

	useEffect(() => {
		// This is for resize event, and due to resize carousel page becomes over the last page when width grows.
		if (carouselPageInfo.lastPageNum < curPageNum) setCurPageNum(carouselPageInfo.lastPageNum);
	}, [carouselPageInfo.lastPageNum, curPageNum]);
	if (totalCount === 0) return null;

	const numCardSkeleton = totalCount - designNodes.length;

	const firePaginationAnalytics = (direction: 'left' | 'right') =>
		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'designsPagination', {
			direction,
		});

	return (
		<Box xcss={wrapperStyles}>
			<WidthObserver setWidth={setCarouselWidth} />
			<CarouselWrapper>
				<CarouselSlider widthOffset={curPageNum * numFullyRenderedCards * (CARD_WIDTH + CARD_GAP)}>
					{designNodes.map((designNode) => (
						// id shouldn't be null in real world. https://atlassian.slack.com/archives/C01E2MPPLE4/p1709250064060249
						<DesignCard key={designNode.id ?? ''} devOpsDesign={designNode} jiraVersion={data} />
					))}
					{/* Checking `hasNext` here because `totalCount` isn't guaranteed to be accurate. */}
					{/* This should avoid rendering extra skeletons when all designs are loaded. */}
					{hasNext &&
						range(numCardSkeleton).map((n) => (
							<DesignCardSkeleton
								key={n}
								onVisible={() => {
									if (!hasNext || isLoadingNext) return null;

									loadNext(6);
								}}
							/>
						))}
				</CarouselSlider>
			</CarouselWrapper>
			{/* Hide the pagination buttons if all cards are visible. */}
			{/* Note: including the `Math.max` guard as `totalCount` may be an approximate value. */}
			{numFullyRenderedCards < Math.max(totalCount, designNodes.length) && (
				<ArrowWrapper>
					<Arrow
						direction="LEFT"
						onClick={() => {
							if (curPageNum === 0) return;
							setCurPageNum(curPageNum - 1);
							firePaginationAnalytics('left');
						}}
					/>
					<Arrow
						direction="RIGHT"
						onClick={() => {
							if (carouselPageInfo.currentEnd >= totalCount) return;
							setCurPageNum(curPageNum + 1);
							firePaginationAnalytics('right');
						}}
					/>
				</ArrowWrapper>
			)}
			<p css={pageCounterStyles}>
				{formatMessage(messages.carouselPageCount, {
					...carouselPageInfo,
					totalCount,
				})}
			</p>
		</Box>
	);
};

const wrapperStyles = xcss({
	width: '100%',
	position: 'relative',
	marginTop: 'space.300',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ArrowWrapper = styled.div({
	position: 'absolute',
	top: 0,
	left: 0,
	right: 0,
	bottom: 0,
	pointerEvents: 'none',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CarouselWrapper = styled.div({
	display: 'flex',
	width: '100%',
	overflow: 'hidden',
	alignItems: 'stretch',
	padding: token('space.050', '4px'),
	boxSizing: 'border-box',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CarouselSlider = styled.div<{ widthOffset: number }>({
	display: 'flex',
	width: '100%',
	flex: 1,
	alignItems: 'stretch',
	gap: token('space.200', '16px'),
	transition: 'all 500ms ease-in-out',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	transform: ({ widthOffset }) => `translateX(-${widthOffset}px)`,
});

const pageCounterStyles = css({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
});
